// =============================================================================
// ImageUploader — file picker + preview + upload button
// =============================================================================
// Used for both user avatars and server icons. Caller supplies a current URL
// (for the preview), a name (for initials fallback), an upload function that
// takes a File and returns the new URL, and an optional remove function.
// =============================================================================

import { useRef, useState } from 'react';
import { Avatar, Button, Icon, Spinner } from './ui';

interface Props {
  name: string;
  currentUrl?: string | null;
  size?: number;
  busy?: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove?: () => Promise<void>;
  // Optional caption shown under the avatar
  hint?: string;
}

export function ImageUploader({
  name,
  currentUrl,
  size = 80,
  busy: externalBusy,
  onUpload,
  onRemove,
  hint,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = uploading || !!externalBusy;

  function pickFile() {
    if (busy) return;
    fileInputRef.current?.click();
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the same file can be picked again later
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setError('Please pick an image file');
      return;
    }
    // Hard cap on input size — 20 MB. The processor will resize, but we
    // shouldn't even try to decode something gigantic.
    if (file.size > 20 * 1024 * 1024) {
      setError('Image too large (max 20 MB)');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    if (!onRemove || busy) return;
    setError(null);
    setUploading(true);
    try {
      await onRemove();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={pickFile}
        disabled={busy}
        className="relative group disabled:opacity-50 disabled:cursor-not-allowed"
        title="Click to change image" aria-label="Click to change image"
      >
        <Avatar name={name} url={currentUrl} size={size} />
        {/* Hover overlay */}
        <div
          className="absolute inset-0 rounded-aim flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors"
          style={{ width: size, height: size }}
        >
          {busy ? (
            <Spinner size={20} />
          ) : (
            <Icon name="upload" size={20} className="text-white opacity-0 group-hover:opacity-100" />
          )}
        </div>
      </button>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex gap-2">
          <Button onClick={pickFile} disabled={busy}>
            <Icon name="upload" size={12} />
            {currentUrl ? 'Change' : 'Upload'}
          </Button>
          {currentUrl && onRemove && (
            <Button onClick={handleRemove} disabled={busy} variant="danger">
              <Icon name="trash" size={12} />
              Remove
            </Button>
          )}
        </div>
        {hint && <div className="text-[10px] text-text-subtle">{hint}</div>}
        {error && <div className="text-xs text-danger">{error}</div>}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}
